import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Chip,
  Skeleton,
  alpha,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { TrendingUp as TrendingUpIcon, LocalFireDepartment as HotIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchTopPokemonCards } from '../../services/pricingService';
import { keyframes } from '@emotion/react';

// Animation for "Live" indicator
const pulse = keyframes`
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
  100% { opacity: 1; transform: scale(1); }
`;

interface MarketCard {
  id: string;
  name: string;
  set: string;
  setId: string;
  image: string;
  price: number;
  trend: 'up' | 'down' | 'stable';
}

interface MarketHighlightsProps {
  game: 'onepiece' | 'pokemon';
}

const MarketHighlights: React.FC<MarketHighlightsProps> = ({ game }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [topCards, setTopCards] = useState<MarketCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMarketData = async () => {
      setLoading(true);
      setTopCards([]); // Clear current cards
      
      try {
        let allHighValueCards: MarketCard[] = [];

        if (game === 'onepiece') {
            // One Piece market data is currently unavailable without optcgapi.com
            setTopCards([]);
            setLoading(false);
            return;
        } else {
            // Fetch data from high-value Pokemon sets
            // Evolving Skies (swsh7), 151 (sv3pt5), Paldean Fates (sv4pt5)
            const setsToFetch = [
                { id: 'swsh7', name: 'Evolving Skies' }, 
                { id: 'sv3pt5', name: '151' },
                { id: 'sv4pt5', name: 'Paldean Fates' }
            ];
            
            const promises = setsToFetch.map(s => fetchTopPokemonCards(s.id, 5)); // Increase limit to ensure we get hits
            const results = await Promise.all(promises);

            results.forEach((cards, index) => {
                const setInfo = setsToFetch[index];
                if (!cards || !Array.isArray(cards)) return;
                
                const validCards = cards
                    .filter((c: any) => c.images?.large || c.images?.small)
                    .map((c: any) => {
                        // Safe price extraction
                        const prices = c.tcgplayer?.prices;
                        const price = prices?.holofoil?.market || 
                                     prices?.normal?.market || 
                                     prices?.reverseHolofoil?.market || 
                                     prices?.['1stEditionHolofoil']?.market || 0;
                                     
                        return {
                            id: c.id,
                            name: c.name,
                            set: setInfo.name,
                            setId: setInfo.id,
                            image: c.images?.large || c.images?.small || '',
                            price: price,
                            trend: 'up' as const
                        };
                    })
                    .filter(c => c.price > 0); // Only cards with prices

                allHighValueCards = [...allHighValueCards, ...validCards];
            });
        }

        // Final sort of the combined list
        allHighValueCards.sort((a, b) => b.price - a.price);

        // Keep top 4 for the widget
        setTopCards(allHighValueCards.slice(0, 4));
      } catch (error) {
        console.error('Error loading market highlights:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMarketData();
  }, [game]);

  const renderContent = () => {
      if (loading) {
        return (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        );
      }

      if (topCards.length === 0) {
          return (
              <Box sx={{ width: '100%', textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Typography>No market data available right now.</Typography>
              </Box>
          );
      }

      return (
        <Grid container spacing={3}>
            {topCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={card.id}>
                <Card
                onClick={() => {
                    const path = game === 'pokemon' 
                        ? `/sets/${card.setId}/cards/${card.id}` 
                        : `/onepiece/sets/${card.setId}/cards/${card.id}`;
                    navigate(path);
                }}
                sx={{
                    height: '100%',
                    position: 'relative',
                    overflow: 'visible',
                    cursor: 'pointer',
                    bgcolor: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.background.paper, 0.6)
                        : theme.palette.background.paper,
                    backdropFilter: theme.palette.mode === 'dark' ? 'blur(10px)' : 'none',
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.3s ease',
                    boxShadow: theme.palette.mode === 'light' ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
                    '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: theme.palette.mode === 'light' ? '0 20px 40px rgba(0,0,0,0.1)' : `0 0 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderColor: theme.palette.primary.main,
                    },
                }}
                >
                {/* Rank Badge */}
                <Box
                    sx={{
                    position: 'absolute',
                    top: -10,
                    left: -10,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'black',
                    fontWeight: 'bold',
                    zIndex: 2,
                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                    }}
                >
                    #{index + 1}
                </Box>

                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                    <CardMedia
                        component="img"
                        image={card.image}
                        alt={card.name}
                        sx={{
                            width: 'auto',
                            height: 280,
                            borderRadius: '12px',
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))'
                        }}
                    />
                </Box>
                
                <CardContent sx={{ pt: 0, textAlign: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold" noWrap title={card.name}>
                    {card.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {card.set} • {card.id}
                    </Typography>
                    
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                        <Typography variant="h5" sx={{ color: theme.palette.mode === 'dark' ? (game === 'pokemon' ? '#ffcc00' : '#4fc3f7') : theme.palette.primary.main }} fontWeight="800">
                            ${card.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </Typography>
                    </Stack>
                </CardContent>
                </Card>
            </Grid>
            ))}
        </Grid>
      );
  };

  return (
    <Box sx={{ width: '100%', py: 4, position: 'relative' }}>
        
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                {game === 'pokemon' ? 'Pokémon' : 'One Piece'} 
                <Box component="span" sx={{ color: theme.palette.mode === 'dark' ? (game === 'pokemon' ? '#ffcc00' : '#4fc3f7') : theme.palette.primary.main }}>Market Watch</Box>
            </Typography>
            <Chip
            icon={<HotIcon sx={{ fill: '#ff3d00' }} />}
            label="Live"
            size="small"
            sx={{
                borderColor: '#ff3d00',
                color: '#ff3d00',
                bgcolor: alpha('#ff3d00', 0.1),
                fontWeight: 'bold',
                '& .MuiChip-icon': {
                    animation: `${pulse} 2s infinite ease-in-out`
                }
            }}
            variant="outlined"
            />
        </Stack>
      </Stack>

      {renderContent()}
    </Box>
  );
};

export default MarketHighlights;
